import React, { useState, useMemo, useEffect } from 'react'
import useSWR from 'swr'
import { getCoreRowModel, useReactTable } from '@tanstack/react-table'
import type { Row } from '@tanstack/react-table'
import { Card, CardContent } from '@/components/ui/card'
import Loader from '@/components/ui/Loader'
import EmptyDatas from 'components/ui/EmptyDatas'
import Table from 'components/ui/my-data-table'
import MyPagination from 'components/ui/MyPagination'
import {
  columnCover,
  columnPreview,
  columnName,
  columnPainterFrames,
  columnLevel,
  columnTags,
  columnAvatars,
} from 'components/template-data-table-column'
import DisplayUpdateTime from 'ui/DisplayUpdateTime'
import useSWRMutation from 'swr/mutation'
import { postFetcher } from 'api/swr'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import RedisKeyHint from './RedisKeyHint'
import { ReloadIcon } from '@radix-ui/react-icons'
import { useToast } from '@/components/ui/use-toast'
import { ContentLangs, ProductOptions } from 'config'
import Selector from 'components/ui/Selector'

type FrameListRow = {
  id: string
  state?: number | null
  contentLangs?: number[]
  createTime?: number
  updateTime?: number
}

type FrameNewListPage = {
  items: FrameListRow[]
  hasMore: boolean
  pageEndId: string | null
}

type SyncResult = {
  success?: boolean
  message?: string
  count?: number
}

const PAGE_SIZE = 50

const countryLangOptions = ContentLangs.filter((l) => l.code > 0)

function errMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) {
    return error.message
  }
  return fallback
}

const SYNC_ALL_URL = '/template-lists/frame-list-new-all/sync'

/** frame:list:new:lang{code}:pid{pid}：分页只读 + 同步 */
const NewTemplateLists = () => {
  const [lang, setLang] = useState(9)
  const [pid, setPid] = useState(11)
  const [after, setAfter] = useState<string | null>(null)
  const [afters, setAfters] = useState<string[]>([])

  const basePath = `/template-lists/frame-list-new/${lang}`
  const syncUrl = `${basePath}/sync`
  const listLabel = `New (${
    ContentLangs.find((l) => l.code === lang)?.short ?? lang
  } / pid ${pid})`

  const swrParams = useMemo(
    () => ({
      limit: PAGE_SIZE,
      pid,
      ...(after ? { after } : {}),
    }),
    [after, pid],
  )

  const { data, error, isLoading, mutate } = useSWR<FrameNewListPage>([
    basePath,
    swrParams,
  ])
  const { toast } = useToast()
  const [isSyncDialogOpen, setIsSyncDialogOpen] = useState(false)
  const [isSyncAllDialogOpen, setIsSyncAllDialogOpen] = useState(false)

  const goAfter = (next: string | null) => {
    setAfter(next)
    if (next === null) {
      setAfters([])
    } else {
      const index = afters.indexOf(next)
      if (index === -1) {
        setAfters([...afters, next])
      } else {
        setAfters(afters.slice(0, index + 1))
      }
    }
  }

  useEffect(() => {
    setAfter(null)
    setAfters([])
  }, [lang, pid])

  const nextAfter =
    data?.hasMore && data.pageEndId ? data.pageEndId : null

  const { trigger: runSync, isMutating: isSyncing } = useSWRMutation(
    syncUrl,
    postFetcher,
  )

  const { trigger: runSyncAll, isMutating: isSyncAllSyncing } = useSWRMutation(
    SYNC_ALL_URL,
    postFetcher,
  )

  const syncBusy = isSyncing || isSyncAllSyncing

  const handleSync = async () => {
    if (syncBusy) {
      return
    }
    setIsSyncDialogOpen(false)
    try {
      const result = (await runSync({})) as SyncResult
      toast({
        title: 'New 列表构建任务发送成功',
        description:
          result?.message ||
          '任务已入队，Worker 处理完成后请点击 Refetch 查看列表。',
        className: 'bg-green-500 text-white',
      })
    } catch (err: unknown) {
      console.error('Failed to sync:', err)
      toast({
        title: 'Error',
        description: errMessage(err, '发送失败'),
        variant: 'destructive',
      })
    }
  }

  const handleSyncAll = async () => {
    if (syncBusy) {
      return
    }
    setIsSyncAllDialogOpen(false)
    try {
      const result = (await runSyncAll({})) as SyncResult
      toast({
        title: 'New 列表构建任务发送成功（全部国家）',
        description:
          result?.message ||
          '任务已入队，Worker 处理完成后请按需 Refetch 各语言列表。',
        className: 'bg-green-500 text-white',
      })
    } catch (err: unknown) {
      console.error('Failed to sync all langs:', err)
      toast({
        title: 'Error',
        description: errMessage(err, '发送失败'),
        variant: 'destructive',
      })
    }
  }

  const columns = useMemo(
    () => [
      {
        header: '#',
        cell: ({ row }: { row: Row<FrameListRow> }) => row.index + 1,
      },
      columnCover,
      columnPreview,
      columnName,
      columnPainterFrames,
      columnLevel,
      columnTags,
      columnAvatars,
      {
        header: 'State',
        id: 'onlineState',
        cell: ({ row }: { row: Row<FrameListRow> }) => {
          const d = row.original
          let online: boolean
          if (d.state !== undefined && d.state !== null) {
            online = d.state === 401
          } else {
            const cl = d.contentLangs
            online = Array.isArray(cl) && cl.length > 0
          }
          return (
            <span
              className={
                online
                  ? 'text-green-600 font-medium'
                  : 'text-muted-foreground'
              }
              title={
                d.state !== undefined && d.state !== null
                  ? 'state === 401 为上线，否则为下线'
                  : '无 state 时：contentLangs 非空为上线，否则为下线'
              }
            >
              {online ? '上线' : '下线'}
            </span>
          )
        },
      },
      {
        header: 'CreateTime',
        accessorKey: 'createTime',
        cell: ({ row }: { row: Row<FrameListRow> }) => (
          <DisplayUpdateTime updateTime={row.original.createTime} />
        ),
      },
      {
        header: 'UpdateTime',
        accessorKey: 'updateTime',
        cell: ({ row }: { row: Row<FrameListRow> }) => (
          <DisplayUpdateTime updateTime={row.original.updateTime} />
        ),
      },
    ],
    [],
  )

  const table = useReactTable({
    data: data?.items ?? [],
    columns,
    enableRowSelection: false,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
  })

  const langSelector = (
    <div className="flex flex-wrap items-center gap-4">
      <div className="flex items-center gap-2">
        <label className="shrink-0 text-sm font-medium">Lang:</label>
        <Selector
          datas={countryLangOptions}
          labelName="Lang"
          value={lang}
          update={setLang}
          showCode
          width="w-[220px]"
        />
      </div>
      <div className="flex items-center gap-2">
        <label className="shrink-0 text-sm font-medium">Pid:</label>
        <Selector
          datas={ProductOptions}
          labelName="Pid"
          value={pid}
          update={setPid}
          showCode
          width="w-[220px]"
        />
      </div>
      <RedisKeyHint keys={[`frame:list:new:lang${lang}:pid${pid}`]} />
    </div>
  )

  if (isLoading && !data) {
    return (
      <div className="space-y-4">
        {langSelector}
        <Loader />
      </div>
    )
  }
  if (!data || error) {
    return (
      <div className="space-y-4">
        {langSelector}
        <EmptyDatas />
      </div>
    )
  }

  return (
    <div className="space-y-4">

      <div className="flex-shrink-0 mb-4">
        <div className="flex flex-wrap items-center gap-4">
          <MyPagination
            nextAfter={nextAfter}
            after={after}
            goAfter={goAfter}
            afters={afters}
            className="w-auto m-0"
          />
          <Button variant="outline" onClick={() => mutate()}>
            Refetch
          </Button>
          {langSelector}
          <Dialog open={isSyncDialogOpen} onOpenChange={setIsSyncDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>从 Firestore 同步（当前国家 × 全部产品）</DialogTitle>
                <DialogDescription>
                  扫描 Firestore，重建当前选中国家（
                  {ContentLangs.find((l) => l.code === lang)?.short ?? lang}
                  ）下 pid 11/12/16 三个 New 榜。上方 pid
                  选择器只影响列表查看，不影响本次同步范围。完成后请 Refetch。
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setIsSyncDialogOpen(false)}
                  disabled={syncBusy}
                >
                  取消
                </Button>
                <Button
                  onClick={handleSync}
                  disabled={syncBusy}
                  variant="default"
                >
                  {isSyncing ? '发送中…' : '确认'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={isSyncAllDialogOpen} onOpenChange={setIsSyncAllDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>从 Firestore 同步所有国家和产品</DialogTitle>
                <DialogDescription>
                  扫描 Firestore，重建全部语言区 × pid 11/12/16 的 New
                  榜。负载与耗时较大，确认后继续。
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setIsSyncAllDialogOpen(false)}
                  disabled={syncBusy}
                >
                  取消
                </Button>
                <Button
                  onClick={handleSyncAll}
                  disabled={syncBusy}
                  variant="default"
                >
                  {isSyncAllSyncing ? '发送中…' : '确认'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button
            variant="outline"
            onClick={() => setIsSyncDialogOpen(true)}
            disabled={syncBusy}
            className="gap-2"
          >
            <ReloadIcon className="w-4 h-4" />
            从 Firestore 同步（当前国家 × 全部产品）
          </Button>
          <Button
            variant="outline"
            onClick={() => setIsSyncAllDialogOpen(true)}
            disabled={syncBusy}
            className="gap-2"
          >
            <ReloadIcon className="w-4 h-4" />
            从 Firestore 同步所有国家和产品
          </Button>
        </div>
      </div>
      <div className="flex-1 h-[calc(100vh-254px)]">
        <Card className="h-full flex flex-col">
          <CardContent className="flex-1 overflow-hidden pl-4 pr-0 pb-0">
            <div className="h-full overflow-auto">
              {data.items.length === 0 ? (
                <EmptyDatas />
              ) : (
                <Table table={table} />
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default NewTemplateLists
